/**
 * 品牌配置加载工具
 * 主站由 brandIsolation 插件在 Vue 编译前裁剪映射；预览应用保留运行时选择。
 *
 * @example
 * ```ts
 * import { loadBrandConfig } from '@/utils/loadBrandConfig'
 *
 * const config = loadBrandConfig({
 *   rocnovel: rocnovelConfig,
 *   oceread: ocereadConfig,
 *   ficmoon: ficmoonConfig,
 * })
 * ```
 */

import type { BrandName } from '../../configs/types'
import { selectBrandKey } from '../../configs/brand-resolution'

type BrandValueMap<T> = Partial<Record<BrandName, T>>
declare const __BRAND__: BrandName

function resolveBrandValue<T>(configs: BrandValueMap<T>): T {
  const key = selectBrandKey(__BRAND__, Object.keys(configs))
  if (key && configs[__BRAND__] !== undefined)
    return configs[__BRAND__] as T
  throw new Error(`Missing explicit brand config for ${__BRAND__}`)
}

/**
 * 加载品牌配置
 *
 * 主站构建插件根据当前品牌裁剪映射和未使用导入，
 * 只保留当前品牌的配置，移除其他品牌的配置
 */
export function loadBrandConfig<T>(configs: BrandValueMap<T>): T {
  return resolveBrandValue(configs)
}

/**
 * 加载品牌专属组件
 *
 * 主站构建插件根据当前品牌裁剪映射和未使用导入，
 * 只保留当前品牌的组件，移除其他品牌的组件
 */
export function loadBrandComponent<T extends BrandValueMap<unknown>>(components: T): T[keyof T] {
  return resolveBrandValue(components) as T[keyof T]
}
